import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Lock, Send, CheckCircle } from 'lucide-react';
import type { PedidoCardapio, RestauranteCardapio, StatusPedido } from '../../types/cardapio';
import { LABEL_STATUS_PEDIDO } from '../../types/cardapio';
import { listarPedidos, salvarPedidos } from '../../utils/cardapio/persistencia';

function formatarPreco(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function CardapioPainel({ restaurante }: { restaurante: RestauranteCardapio }) {
  const [senha, setSenha] = useState('');
  const [autenticado, setAutenticado] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoCardapio[]>([]);

  const recarregar = useCallback(() => {
    setPedidos(listarPedidos(restaurante.slug));
  }, [restaurante.slug]);

  useEffect(() => {
    if (!autenticado) return;
    recarregar();
    const id = window.setInterval(recarregar, 5000);
    return () => window.clearInterval(id);
  }, [autenticado, recarregar]);

  const atualizarStatus = (pedidoId: string, status: StatusPedido) => {
    const lista = listarPedidos(restaurante.slug);
    const idx = lista.findIndex((p) => p.id === pedidoId);
    if (idx < 0) return;
    lista[idx] = { ...lista[idx], status, atualizadoEm: new Date().toISOString() };
    if (
      restaurante.modoCozinha === 'junto' &&
      status === 'pago' &&
      lista[idx].status === 'pago'
    ) {
      lista[idx].status = 'enviado_cozinha';
    }
    salvarPedidos(restaurante.slug, lista);
    recarregar();
  };

  const confirmarPagamento = (p: PedidoCardapio) => {
    const lista = listarPedidos(restaurante.slug);
    const idx = lista.findIndex((x) => x.id === p.id);
    if (idx < 0) return;
    const prox: StatusPedido =
      restaurante.modoCozinha === 'junto' ? 'enviado_cozinha' : 'pago';
    lista[idx] = {
      ...lista[idx],
      status: prox,
      atualizadoEm: new Date().toISOString(),
    };
    salvarPedidos(restaurante.slug, lista);
    recarregar();
  };

  const enviarCozinha = (p: PedidoCardapio) => {
    atualizarStatus(p.id, 'enviado_cozinha');
  };

  if (!autenticado) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <form
          className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full border"
          onSubmit={(e) => {
            e.preventDefault();
            if (senha === restaurante.senhaPainel) setAutenticado(true);
            else alert('Senha incorreta.');
          }}
        >
          <Lock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
          <h1 className="text-center font-bold text-black">Balcão — {restaurante.nome}</h1>
          <input
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="Senha do painel"
            className="mt-4 w-full border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            className="mt-3 w-full py-2 rounded-lg bg-orange-600 text-white font-semibold text-sm"
          >
            Entrar
          </button>
        </form>
      </div>
    );
  }

  const pendentes = pedidos.filter((p) => p.status === 'aguardando_pagamento');
  const naFila = pedidos.filter((p) => p.status === 'pago');
  const outros = pedidos.filter((p) =>
    ['enviado_cozinha', 'preparando', 'pronto', 'entregue'].includes(p.status)
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-orange-700 text-white px-4 py-3">
        <h1 className="font-bold">Balcão — {restaurante.nome}</h1>
        <p className="text-xs text-orange-200">
          Modo: {restaurante.modoCozinha === 'junto' ? 'balcão + cozinha juntos' : 'cozinha separada'}
        </p>
      </header>
      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {pendentes.length > 0 && (
          <Secao titulo="Aguardando pagamento" pedidos={pendentes} render={(p) => (
            <button
              type="button"
              onClick={() => confirmarPagamento(p)}
              className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-lg inline-flex items-center gap-1"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Confirmar pagamento
            </button>
          )} />
        )}
        {restaurante.modoCozinha === 'separado' && naFila.length > 0 && (
          <Secao titulo="Pagos — enviar à cozinha" pedidos={naFila} render={(p) => (
            <button
              type="button"
              onClick={() => enviarCozinha(p)}
              className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-300 px-2 py-1 rounded-lg inline-flex items-center gap-1"
            >
              <Send className="w-3.5 h-3.5" />
              Enviar à cozinha
            </button>
          )} />
        )}
        <Secao titulo="Em andamento" pedidos={outros} />
        {pedidos.length === 0 && (
          <p className="text-center text-neutral-500 text-sm py-12">Nenhum pedido ainda.</p>
        )}
      </main>
    </div>
  );
}

function Secao({
  titulo,
  pedidos,
  render,
}: {
  titulo: string;
  pedidos: PedidoCardapio[];
  render?: (p: PedidoCardapio) => ReactNode;
}) {
  if (pedidos.length === 0) return null;
  return (
    <section>
      <h2 className="text-xs font-bold uppercase text-neutral-500 mb-2">{titulo}</h2>
      <ul className="space-y-2">
        {pedidos.map((p) => (
          <li key={p.id} className="bg-white rounded-xl border p-3 text-sm">
            <div className="flex justify-between">
              <span className="font-bold">Mesa {p.mesa}</span>
              <span className="text-orange-600 font-bold">{formatarPreco(p.total)}</span>
            </div>
            <p className="text-[11px] text-neutral-500">
              {LABEL_STATUS_PEDIDO[p.status]} · {p.formaPagamento}
            </p>
            <ul className="text-xs mt-1 text-neutral-700">
              {p.itens.map((it, i) => (
                <li key={i}>
                  {it.quantidade}× {it.nome}
                </li>
              ))}
            </ul>
            {render?.(p)}
          </li>
        ))}
      </ul>
    </section>
  );
}
